/** Model-free display projection of an existing setup; no generation identity or analytical policy changes. */
import * as z from 'zod';
import { researchProvenanceSchema } from '@testfeed/integration-contracts';
/** References the producer's published IDs. A real-presentation test guards this binding without changing
 * the analytical producer solely to extract UI string constants. */
export const OPPORTUNITY_SETUP_PLACEMENT = {
    sectionId: 'paid-product-history',
    listId: 'paid-product-recurrence',
};
const text = z.string().trim().min(1);
const productId = z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/);
export const currentInsightSetupSchema = z
    .object({
    batchId: text,
    research: researchProvenanceSchema.optional(),
    productId,
    productTitle: text,
    fieldLabel: text,
    question: text,
    reason: text,
    current: text,
    alternatives: z
        .array(z.object({ title: text, value: text }))
        .min(1)
        .max(3),
    audienceLabel: text,
    figure: z.object({ customers: z.number().int().nonnegative(), buyers: z.number().int().positive() }),
    source: z.object({
        insightId: text,
        currentInsightId: text,
        definitionId: z.literal('product-history'),
        revision: text,
        sectionId: z.literal(OPPORTUNITY_SETUP_PLACEMENT.sectionId),
        listId: z.literal(OPPORTUNITY_SETUP_PLACEMENT.listId),
        rowId: productId,
    }),
    /** The saved finding used to prepare this comparison, kept separate from the current finding above. */
    preparedEvidence: z.object({
        customers: z.number().int().nonnegative(),
        buyers: z.number().int().positive(),
        ordersSyncedAt: z.string(),
        preparedAt: z.string(),
    }),
})
    .refine((setup) => setup.source.rowId === setup.productId, 'Opportunity source row does not match its product');
export const currentInsightSetupsSchema = z.object({ setups: z.array(currentInsightSetupSchema) });
/** Formatting only: placement/figures use the current source; preparedEvidence/control stay immutable. */
export function buildInsightSetupDisplay(batchId, setup, source) {
    const picked = setup.choice.shortlist.find((field) => field.researchInputId === setup.choice.pick.researchInputId);
    if (!picked)
        throw new Error('OPPORTUNITY_DISPLAY_FIELD_MISSING: chosen field is not in the saved shortlist');
    return currentInsightSetupSchema.parse({
        source: {
            ...OPPORTUNITY_SETUP_PLACEMENT,
            ...source,
            insightId: setup.origin.insightId,
            definitionId: setup.origin.definitionId,
            rowId: setup.product.productId,
        },
        batchId,
        research: setup.research ?? { status: 'not_requested' },
        productId: setup.product.productId,
        productTitle: setup.product.title,
        fieldLabel: picked.label,
        question: setup.question.research,
        reason: setup.choice.pick.reason,
        current: setup.control.value,
        alternatives: setup.alternatives.map(({ title, value }) => ({ title, value })),
        audienceLabel: setup.audience.label,
        figure: source.currentFigure ?? setup.origin.figure,
        preparedEvidence: {
            ...setup.origin.figure,
            ordersSyncedAt: setup.origin.ordersSyncedAt,
            preparedAt: setup.trace.generatedAt,
        },
    });
}
//# sourceMappingURL=opportunity-setup-display.js.map